import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import PriceRangeSlider from './PriceRangeSlider';
import catalogService from '../../services/catalogService';

const CatalogFilters = ({ filters, onFilterChange, filterData, loadingStates, onPriceChange, clearFilters }) => {
    const [canalesData, setCanalesData] = useState([]);
    const [categoriasData, setCategoriasData] = useState([]);
    const [subcategoriasData, setSubcategoriasData] = useState([]);
    const [marcasData, setMarcasData] = useState([]);
    const [conceptosData, setConceptosData] = useState([]);
    const [loadingStatesLocal, setLoadingStates] = useState({
        canales: false,
        categorias: false,
        subcategorias: false,
        marcas: false,
        conceptos: false
    });

    // Usar datos de canales y categorías del contexto (centralizados en useCatalogState)
    useEffect(() => {
        if (filterData?.canales) {
            setCanalesData(filterData.canales);
        }
    }, [filterData?.canales]);

    useEffect(() => {
        if (filterData?.categorias) {
            setCategoriasData(filterData.categorias);
        }
    }, [filterData?.categorias]);

    // Cargar subcategorías cuando cambia la categoría
    useEffect(() => {
        const loadSubcategorias = async () => {
            if (!filters.categoria) {
                setSubcategoriasData([]);
                return;
            }

            setLoadingStates(prev => ({ ...prev, subcategorias: true }));
            try {
                // Usar catalogService con base URL desde .env
                const data = await catalogService.getSubcategories(filters.categoria);
                if (data.exito && data.datos) {
                    setSubcategoriasData(data.datos);
                }
            } catch (error) {
                console.error('Error cargando subcategorías:', error);
                setSubcategoriasData([]);
            } finally {
                setLoadingStates(prev => ({ ...prev, subcategorias: false }));
            }
        };

        loadSubcategorias();
    }, [filters.categoria]); // Solo depende del filtro de categoría

    // Cargar marcas - SOLO cuando hay categoría seleccionada
    useEffect(() => {
        const loadMarcas = async () => {
            if (!filters.categoria) {
                setMarcasData([]);
                return;
            }
            
            if (marcasData.length > 0 && marcasData[0]?.categoria === filters.categoria) {
                return;
            }
            
            setLoadingStates(prev => ({ ...prev, marcas: true }));
            
            try {
                // Usar catalogService con base URL desde .env
                const data = await catalogService.getBrandsByCategory(filters.categoria);
                
                if (data.exito && data.datos) {
                    setMarcasData(data.datos);
                } else {
                    setMarcasData([]);
                }
            } catch (error) {
                console.error('❌ loadMarcas - Error:', error);
                setMarcasData([]);
            } finally {
                setLoadingStates(prev => ({ ...prev, marcas: false }));
            }
        };

        loadMarcas();
    }, [filters.categoria]); // Depende de la categoría seleccionada

    // Cargar conceptos - SOLO cuando hay categoría seleccionada
    useEffect(() => {
        const loadConceptos = async () => {
            if (!filters.categoria) {
                setConceptosData([]);
                return;
            }
            
            if (conceptosData.length > 0 && conceptosData[0]?.categoria === filters.categoria) {
                return;
            }
            
            setLoadingStates(prev => ({ ...prev, conceptos: true }));
            
            try {
                // Usar catalogService con base URL desde .env
                const data = await catalogService.getConceptsByCategory(filters.categoria);
                
                if (data.exito && data.datos) {
                    setConceptosData(data.datos);
                } else {
                    setConceptosData([]);
                }
            } catch (error) {
                console.error('❌ loadConceptos - Error:', error);
                setConceptosData([]);
            } finally {
                setLoadingStates(prev => ({ ...prev, conceptos: false }));
            }
        };

        loadConceptos();
    }, [filters.categoria]); // Depende de la categoría seleccionada

    // Limpiar filtros dependientes cuando cambia la categoría
    useEffect(() => {
        if (filters.categoria) {
            if (filters.marca) {
                onFilterChange('marca', null);
            }
            if (filters.linea) {
                onFilterChange('linea', null);
            }
            if (filters.concepto) {
                onFilterChange('concepto', null);
            }
        }
    }, [filters.categoria]);

    // Limpiar línea cuando cambia la marca
    useEffect(() => {
        if (filters.marca) {
            if (filters.linea) {
                onFilterChange('linea', null);
            }
        }
    }, [filters.marca]);
    const getCanalesOptions = () => {
        if (loadingStatesLocal.canales) return [{ label: 'Cargando...', value: null, disabled: true }];
        if (canalesData.length === 0) return [{ label: 'Sin opciones', value: null, disabled: true }];
        
        return [
            { label: 'Todos los canales', value: null },
            ...canalesData.map(canal => ({ label: canal.nombre, value: canal.id }))
        ];
    };

    const getCategoriasOptions = () => {
        if (loadingStatesLocal.categorias) return [{ label: 'Cargando...', value: null, disabled: true }];
        if (categoriasData.length === 0) return [{ label: 'Sin opciones', value: null, disabled: true }];
        
        return [
            { label: 'Todas las categorías', value: null },
            ...categoriasData.map(categoria => ({ label: categoria.nombre, value: categoria.id }))
        ];
    };

    const getSubcategoriasOptions = () => {
        if (!filters.categoria) return [{ label: 'Seleccione una categoría', value: null, disabled: true }];
        if (loadingStatesLocal.subcategorias) return [{ label: 'Cargando...', value: null, disabled: true }];
        if (subcategoriasData.length === 0) return [{ label: 'Sin subcategorías', value: null, disabled: true }];
        
        return [
            { label: 'Todas las subcategorías', value: null },
            ...subcategoriasData.map(subcategoria => ({ label: subcategoria.nombre, value: subcategoria.id }))
        ];
    };

    const getMarcasOptions = () => {
        if (!filters.categoria) return [{ label: 'Seleccione una categoría', value: null, disabled: true }];
        if (loadingStatesLocal.marcas) return [{ label: 'Cargando...', value: null, disabled: true }];
        if (marcasData.length === 0) return [{ label: 'Sin marcas', value: null, disabled: true }];
        
        return [
            { label: 'Todas las marcas', value: null },
            ...marcasData.map(marca => ({ label: marca.nombre, value: marca.id }))
        ];
    };

    const getLineasOptions = () => {
        if (!filterData) return [{ label: 'Cargando...', value: null, disabled: true }];
        const lineasFromContext = filterData.lineas || [];
        if (lineasFromContext.length === 0) return [{ label: 'Sin líneas', value: null, disabled: true }];
        return [
            { label: 'Todas las líneas', value: null },
            ...lineasFromContext.map(linea => ({ label: linea.nombre, value: linea.id }))
        ];
    };

    const getConceptosOptions = () => {
        if (!filters.categoria) return [{ label: 'Seleccione una categoría', value: null, disabled: true }];
        if (loadingStatesLocal.conceptos) return [{ label: 'Cargando...', value: null, disabled: true }];
        if (conceptosData.length === 0) return [{ label: 'Sin conceptos', value: null, disabled: true }];
        
        return [
            { label: 'Todos los conceptos', value: null },
            ...conceptosData.map(concepto => ({ label: concepto.nombre, value: concepto.id }))
        ];
    };

    // Manejadores de cambio individuales para cada filtro
    const handleCanalChange = (e) => {
        const value = e.value;
        onFilterChange('canal', value);
    };

    const handleCategoriaChange = (e) => {
        const value = e.value;
        onFilterChange('categoria', value);
    };

    const handleSubcategoriaChange = (e) => {
        const value = e.value;
        if (value !== undefined) {
            onFilterChange('subcategoria', value);
        }
    };

    const handleMarcaChange = (e) => {
        const value = e.value;
        if (value !== undefined) {
            onFilterChange('marca', value);
        }
    };

    const handleLineaChange = (e) => {
        const value = e.value;
        if (value !== undefined) {
            onFilterChange('linea', value);
        }
    };

    const handleConceptoChange = (e) => {
        const value = e.value;
        if (value !== undefined) {
            onFilterChange('concepto', value);
        }
    };

    return (
        <div className="bg-primary-active p-4 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4 text-900">Filtros</h3>
            
            {/* Canal Dropdown - Independiente */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Canal</label>
                <Dropdown value={filters.canal} options={getCanalesOptions()} onChange={handleCanalChange} placeholder="Seleccionar canal" className="w-full" disabled={loadingStatesLocal.canales} />
            </div>
            
            {/* Categoría Dropdown - Independiente */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Categoría</label>
                <Dropdown value={filters.categoria} options={getCategoriasOptions()} onChange={handleCategoriaChange} placeholder="Seleccionar categoría" className="w-full" disabled={loadingStatesLocal.categorias} />
            </div>
            
            {/* Subcategoría Dropdown - Dependiente solo de categoría */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Subcategoría</label>
                <Dropdown value={filters.subcategoria} options={getSubcategoriasOptions()} onChange={handleSubcategoriaChange} placeholder="Seleccionar subcategoría" className="w-full" disabled={loadingStatesLocal.subcategorias || !filters.categoria} />
            </div>
            
            {/* Marca Dropdown - Independiente */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Marca</label>
                <Dropdown value={filters.marca} options={getMarcasOptions()} onChange={handleMarcaChange} placeholder="Seleccionar marca" className="w-full" disabled={loadingStatesLocal.marcas} />
            </div>
            
            {/* Línea Dropdown - Dependiente solo de marca */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Línea</label>
                <Dropdown value={filters.linea} options={getLineasOptions()} onChange={handleLineaChange} placeholder="Seleccionar línea" className="w-full" disabled={loadingStates.lineas || !filters.marca} />
            </div>
            
            {/* Concepto Dropdown - Independiente */}
            <div className="mb-4">
                <label className="block text-900 font-medium mb-2">Concepto</label>
                <Dropdown value={filters.concepto} options={getConceptosOptions()} onChange={handleConceptoChange} placeholder="Seleccionar concepto" className="w-full" disabled={loadingStatesLocal.conceptos} />
            </div>

            <div className="mt-4">
                <PriceRangeSlider onPriceChange={onPriceChange} />
            </div>
            
            <div className="mt-4 flex gap-2">
                <Button label="Limpiar Filtros" icon="pi pi-times" className="p-button-outlined p-button-sm" onClick={clearFilters} />
            </div>
        </div>
    );
};

export default CatalogFilters;
